import { useState } from 'react';
import Cookie from 'js-cookie';
import {
    fadeIn,
    fadeOut,
    slideInLeft,
    slideOutLeft,
    useAnimateInOut
} from '@zoopla-eng/zds-components/animation';
import { useOptimizelyFeatureFlag } from '@zoopla/optimizely';
import * as Dialog from '@radix-ui/react-dialog';
import { IconButton } from '@zoopla-eng/zds-components/button';
import { Link } from '@zoopla-eng/zds-components/link';
import { Box } from '@zoopla-eng/zds-components/box';
import { CloseMedium, MenuMedium } from '@zoopla-eng/zds-components/icons';
import { styled } from '@zoopla-eng/zds-components/themes';
import { DrawerList } from './Header.styles';
import {
    Navigation,
    OptimiselyEnabledNavigation,
    trackAccount,
    trackNav
} from './data';
import { Divider } from '@zoopla-eng/zds-components/divider';

const MenuButtonText = styled('span', {
    color: '$textBody',
    fontGroup: 'zester',
    fontWeight: '$400'
});

const StyledOverlay = styled(Dialog.Overlay, {
    position: 'fixed',
    inset: 0,
    bg: '$functionalScrim',
    // Need to change elevation of overlay
    zIndex: '$modal',
    '&[data-state="open"]': {
        animation: `${fadeIn} $transitions$durationS100 $transitions$timingS100`
    },
    '&[data-state="closed"]': {
        animation: `${fadeOut} $transitions$durationS100 $transitions$timingS100`
    }
});
StyledOverlay.displayName = 'Overlay';

const StyledContent = styled(Dialog.Content, {
    // Need to change elevation of Menu token
    zIndex: '$modal',
    position: 'fixed',
    top: '0',
    bg: '$graphicBackgroundDefault',
    fontGroup: 'mug',
    boxShadow: '$900',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '&:focus': { outline: 'none' },
    minWidth: '$sizes$queryS100',
    maxWidth: 'min($sizes$queryM100, caxlc(100% - $space$M400))',
    height: '100%',
    overflowY: 'auto',
    transform: 'none',
    borderRadius: '0',
    left: '0',
    right: 'inherit',
    '&[data-state="open"]': {
        animation: `${slideInLeft} $transitions$durationS100 $transitions$timingS100`
    },
    '&[data-state="closed"]': {
        animation: `${slideOutLeft} $transitions$durationS100 $transitions$timingS100`
    }
});
StyledContent.displayName = 'Content';

interface MenuProps {
    isLoggedIn: boolean;
    signinUrl: string;
}

function Menu({ isLoggedIn, signinUrl }: MenuProps) {
    const [dialogIsOpen, setDialogIsOpen] = useState(false);

    const onTriggerClick = () => {
        setDialogIsOpen(!dialogIsOpen);
    };

    const onClose = () => {
        setDialogIsOpen(false);
    };

    const { shouldRender, onAnimationEnd, animationState } = useAnimateInOut(
        dialogIsOpen ?? false
    );

    const [isInstantValuationEnabled] = useOptimizelyFeatureFlag(
        '_home-values_instant-valuation-menu'
    );

    const navigationArray = isInstantValuationEnabled
        ? [...Navigation, ...OptimiselyEnabledNavigation]
        : Navigation;

    return (
        <Dialog.Root open={dialogIsOpen}>
            <Dialog.Trigger asChild onClick={onTriggerClick}>
                <IconButton
                    color="$textBody"
                    category="tertiary"
                    label="Open Menu"
                    data-testid="header-mobile-hamburger"
                    data-state={animationState === 'in' ? 'open' : 'closed'}
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        fontGroup="zester"
                    >
                        <MenuMedium />
                        <MenuButtonText>Menu</MenuButtonText>
                    </Box>
                </IconButton>
            </Dialog.Trigger>
            {shouldRender && (
                <Dialog.Portal>
                    <StyledOverlay
                        onAnimationEnd={onAnimationEnd}
                        data-state={animationState === 'in' ? 'open' : 'closed'}
                        onClick={onClose}
                    />

                    <StyledContent
                        aria-label="Main menu"
                        aria-labelledby={undefined}
                        aria-describedby={undefined}
                        data-state={animationState === 'in' ? 'open' : 'closed'}
                        onEscapeKeyDown={onClose}
                        onAnimationEnd={() => {
                            document.body.style.removeProperty(
                                'pointer-events'
                            );
                        }}
                    >
                        <Box p="$S200">
                            <Dialog.Trigger asChild onClick={onTriggerClick}>
                                <IconButton
                                    category="tertiary"
                                    label="Close Menu"
                                >
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        fontGroup="zester"
                                    >
                                        <CloseMedium />
                                    </Box>
                                </IconButton>
                            </Dialog.Trigger>
                        </Box>
                        <nav aria-label="Main menu">
                            <DrawerList>
                                {navigationArray.map(({ title, href }, idx) => (
                                    <Box key={href} display="flex" as="li">
                                        <Link
                                            href={href}
                                            onClick={
                                                title === 'My home'
                                                    ? trackAccount(
                                                          title.toLowerCase()
                                                      )
                                                    : trackNav(title)
                                            }
                                            muted
                                            withIcon
                                            disableVisited
                                        >
                                            {title}
                                        </Link>
                                    </Box>
                                ))}
                                <Box key={'/my-zoopla/'} display="flex" as="li">
                                    <Link
                                        href={'/my-zoopla/'}
                                        onClick={trackNav('/my-zoopla/')}
                                        muted
                                        withIcon
                                        disableVisited
                                    >
                                        <span>Account</span>
                                    </Link>
                                </Box>
                            </DrawerList>
                            <Box py="$S400">
                                <Divider direction="horizontal" />
                            </Box>
                            <Box pt="$S500" px="$S500" pb="$M200">
                                {!isLoggedIn ? (
                                    <Link
                                        href={signinUrl}
                                        onClick={trackAccount('sign in')}
                                        muted
                                        withIcon
                                        disableVisited
                                    >
                                        <span>Sign in</span>
                                    </Link>
                                ) : (
                                    <Link
                                        href="/?signout=1"
                                        onClick={() => Cookie.remove('adb_he')}
                                        muted
                                        withIcon
                                        disableVisited
                                    >
                                        <span>Sign out</span>
                                    </Link>
                                )}
                            </Box>
                        </nav>
                    </StyledContent>
                </Dialog.Portal>
            )}
        </Dialog.Root>
    );
}
Menu.displayName = 'Menu';

export default Menu;
